import {
  Box,
  CircularProgress,
  Divider,
  Dropdown,
  FormControl,
  FormLabel,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  Option,
  Select,
  Sheet,
  Table,
  Typography,
} from '@mui/joy';
import React from 'react';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { visuallyHidden } from '@mui/utils';
import { deutschNumberFormatter } from '../helper/utils';
import { IInvoice } from '../interface/IInvoice';
import dayjs from 'dayjs';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import invoiceApi from '../api/invoice';
import { useToast } from '../helper/ToastProvider';

function labelDisplayedRows({ from, to, count }: { from: number; to: number; count: number }) {
  return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

interface HeadCell {
  disablePadding: boolean;
  id: string;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'invoiceId',
    numeric: false,
    disablePadding: true,
    label: 'ID',
  },
  {
    id: 'offerId',
    numeric: false,
    disablePadding: false,
    label: 'Offer ID',
  },
  {
    id: 'offerTitle',
    numeric: false,
    disablePadding: false,
    label: 'Offer Name',
  },
  {
    id: 'servicePeriod',
    numeric: false,
    disablePadding: false,
    label: 'Service Period',
  },
  {
    id: 'totalNet',
    numeric: true,
    disablePadding: false,
    label: 'Total Net',
  },
  {
    id: 'totalGross',
    numeric: true,
    disablePadding: false,
    label: 'Total Gross',
  },
  {
    id: 'invoiceDate',
    numeric: false,
    disablePadding: false,
    label: 'Invoice Date',
  },
  {
    id: 'dueDate',
    numeric: false,
    disablePadding: false,
    label: 'Due Date',
  },
  {
    id: 'paymentDate',
    numeric: false,
    disablePadding: false,
    label: 'Payment Date',
  },
  {
    id: 'status',
    numeric: false,
    disablePadding: false,
    label: 'Status',
  },
  {
    id: 'action',
    numeric: true,
    disablePadding: false,
    label: 'Actions',
  },
];

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, rowCount, onRequestSort } = props;
  const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <thead>
      <tr>
        {headCells.map((headCell) => {
          const active = orderBy === headCell.id;
          return (
            <th key={headCell.id} aria-sort={active ? ({ asc: 'ascending', desc: 'descending' } as const)[order] : undefined}>
              <Link
                underline='none'
                color='neutral'
                textColor={active ? 'primary.plainColor' : undefined}
                component='button'
                onClick={createSortHandler(headCell.id)}
                startDecorator={headCell.numeric ? <ArrowDownwardIcon sx={[active ? { opacity: 1 } : { opacity: 0 }]} /> : null}
                endDecorator={!headCell.numeric ? <ArrowDownwardIcon sx={[active ? { opacity: 1 } : { opacity: 0 }]} /> : null}
                sx={{
                  fontWeight: 'lg',

                  '& svg': {
                    transition: '0.2s',
                    transform: active && order === 'desc' ? 'rotate(0deg)' : 'rotate(180deg)',
                  },

                  '&:hover': { '& svg': { opacity: 1 } },
                }}
              >
                {headCell.label}
                {active ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </Link>
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

function RowMenu({ sendInvoiceEmail, onClickXml, onClickPayment, onClickPdfWithoutVat }: any) {
  return (
    <Dropdown>
      <MenuButton slots={{ root: IconButton }} slotProps={{ root: { variant: 'plain', color: 'neutral', size: 'sm' } }}>
        <MoreHorizRoundedIcon />
      </MenuButton>
      <Menu size='sm' sx={{ minWidth: 140 }}>
        <MenuItem onClick={onClickXml} color='primary'>
          XML
        </MenuItem>
        <MenuItem onClick={onClickPayment} color='primary'>
          Payment
        </MenuItem>
        <MenuItem onClick={onClickPdfWithoutVat} color='primary'>
          Pdf w/o VAT
        </MenuItem>
        <MenuItem onClick={sendInvoiceEmail} color='primary'>
          Send Email
        </MenuItem>
        <Divider />
        <MenuItem color='danger'>Delete</MenuItem>
      </Menu>
    </Dropdown>
  );
}

const InvoiceTable = ({
  order,
  setOrder,
  orderBy,
  setOrderBy,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  invoices,
  total,
  loading,
  onDownloadXml,
  onDownloadExcel,
  onDownloadPdf,
  onDownloadPdfWithoutVat,
  sendInvoiceEmail,
  onPayment,
}: {
  order: any;
  setOrder: any;
  orderBy: any;
  setOrderBy: any;
  page: any;
  setPage: any;
  rowsPerPage: number;
  setRowsPerPage: any;
  invoices: IInvoice[];
  total: number;
  loading: boolean;
  onDownloadXml: (invoiceId: string) => void;
  onDownloadExcel: (invoiceId: string) => void;
  onDownloadPdf: (invoiceId: string) => void;
  onDownloadPdfWithoutVat: (invoiceId: string) => void;
  sendInvoiceEmail: (invoiceId: string) => void;
  onPayment: (selInvoice: IInvoice) => void;
}) => {
  const { showToast } = useToast();

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: any, newValue: number | null) => {
    setRowsPerPage(parseInt(newValue!.toString(), 10));
    setPage(0);
  };
  const getLabelDisplayedRowsTo = () => {
    if (total === -1) {
      return (page + 1) * rowsPerPage;
    }
    return rowsPerPage === -1 ? total : Math.min(total, (page + 1) * rowsPerPage);
  };
  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - total) : 0;

  return (
    <Sheet variant='outlined' sx={{ width: '100%', boxShadow: 'sm', borderRadius: 'sm' }}>
      <Table
        aria-labelledby='tableTitle'
        hoverRow
        sx={{
          '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
          '--Table-headerUnderlineThickness': '1px',
          '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
          '--TableCell-paddingY': '4px',
          '--TableCell-paddingX': '8px',
          '& thead th:nth-child(1)': {
            width: '100px',
          },
          '& thead th:nth-child(3)': {
            width: '20%',
          },
          '& thead th:nth-child(4)': {
            width: '160px',
          },
          '& tr td:nth-child(5)': { textAlign: 'right' },
          '& tr td:nth-child(6)': { textAlign: 'right' },
        }}
      >
        <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} rowCount={total} />
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={11}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 200,
                  }}
                >
                  <CircularProgress size='lg' />
                </Box>
              </td>
            </tr>
          ) : (
            [...invoices]
              // .sort(getComparator(order, orderBy))
              .map((row, index) => {
                const labelId = `enhanced-table-checkbox-${index}`;
                return (
                  <tr tabIndex={-1} key={row.offerId}>
                    <td id={labelId}>
                      <Typography level='body-xs'>{row.invoiceId}</Typography>
                    </td>
                    <td>
                      <Typography level='body-xs'>{row.offerId}</Typography>
                    </td>
                    <td>
                      <Typography level='body-xs'>{row.offer?.title || ''}</Typography>
                    </td>
                    <td>
                      <Typography level='body-xs'>
                        {dayjs(row.invoiceFrom).format('DD.MM.YYYY') || ''} - {dayjs(row.invoiceTo).format('DD.MM.YYYY') || ''}
                      </Typography>{' '}
                    </td>
                    <td>
                      <Typography level='body-xs'>{deutschNumberFormatter(row.totalNetAmount || 0)} €</Typography>
                    </td>
                    <td>
                      <Typography level='body-xs'>{deutschNumberFormatter(row.totalGrossAmount || 0)} €</Typography>
                    </td>
                    <td>
                      <Typography level='body-xs'>{dayjs(row.invoiceDate).format('DD.MM.YYYY') || ''}</Typography>
                    </td>
                    <td>
                      <Typography level='body-xs'>{row.dueDate ? dayjs(row.dueDate).format('DD.MM.YYYY') : '-'}</Typography>
                    </td>
                    <td>
                      <Typography level='body-xs'>{row.dateOfPayment ? dayjs(row.dateOfPayment).format('DD.MM.YYYY') : '-'}</Typography>
                    </td>
                    <td>
                      <Typography level='body-xs'>{row.status}</Typography>
                    </td>
                    <td>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Link onClick={() => onDownloadPdf(row.invoiceId)}>Pdf</Link>
                        <Link onClick={() => onDownloadExcel(row.invoiceId)}>Excel</Link>
                        <RowMenu
                          onClickXml={() => onDownloadXml(row.invoiceId)}
                          onClickPayment={() => onPayment(row)}
                          onClickPdfWithoutVat={() => onDownloadPdfWithoutVat(row.invoiceId)}
                          sendInvoiceEmail={() => sendInvoiceEmail(row.invoiceId)}
                        />
                      </Box>
                    </td>
                  </tr>
                );
              })
          )}
          {emptyRows > 0 && (
            <tr
              style={
                {
                  height: `calc(${emptyRows} * 40px)`,
                  '--TableRow-hoverBackground': 'transparent',
                } as React.CSSProperties
              }
            >
              <td colSpan={6} aria-hidden />
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={11}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  justifyContent: 'flex-end',
                }}
              >
                <FormControl orientation='horizontal' size='sm'>
                  <FormLabel>Rows per page:</FormLabel>
                  <Select onChange={handleChangeRowsPerPage} value={rowsPerPage}>
                    <Option value={5}>5</Option>
                    <Option value={10}>10</Option>
                    <Option value={25}>25</Option>
                  </Select>
                </FormControl>
                <Typography sx={{ textAlign: 'center', minWidth: 80 }}>
                  {labelDisplayedRows({
                    from: total === 0 ? 0 : page * rowsPerPage + 1,
                    to: getLabelDisplayedRowsTo(),
                    count: total === -1 ? -1 : total,
                  })}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    size='sm'
                    color='neutral'
                    variant='outlined'
                    disabled={page === 0}
                    onClick={() => handleChangePage(page - 1)}
                    sx={{ bgcolor: 'background.surface' }}
                  >
                    <KeyboardArrowLeftIcon />
                  </IconButton>
                  <IconButton
                    size='sm'
                    color='neutral'
                    variant='outlined'
                    disabled={total !== -1 ? page >= Math.ceil(total / rowsPerPage) - 1 : false}
                    onClick={() => handleChangePage(page + 1)}
                    sx={{ bgcolor: 'background.surface' }}
                  >
                    <KeyboardArrowRightIcon />
                  </IconButton>
                </Box>
              </Box>
            </td>
          </tr>
        </tfoot>
      </Table>
    </Sheet>
  );
};

export default InvoiceTable;
